import Grid from "@mui/material/Grid2";
import { Autocomplete, Button, InputAdornment, Paper, TextField, Typography } from "@mui/material";
import PackagesFilterCard from "../PackagesFilterCard/PackagesFilterCard";
import PackagesPieChart from "../PackagesPieChart/PackagesPieChart";
import PackageTable from "../PackageTable/PackageTable";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { Search as SearchIcon } from "@mui/icons-material";
import { useMonitoringPackages } from "../../../../../customHooks/useMonitoringPackages";
import { isGreaterEndDate } from "../../../../../utils/helpers";
import { useDispatch } from "react-redux";
import {
  setPackageDataBeenFetched,
  setPage,
  setSelectedPackage,
  setSelectedPackageDateRange,
} from "../../../../../features/monitoringPackagesSlice";
import dayjs from "dayjs";
import "dayjs/locale/en-gb"; // Import the locale for British English
import { Box, Stack } from "@mui/system";
import { useSearchParams } from "react-router-dom";
import { PackagesEndPointLogic } from "../../../../../logic/DataCollection/Monitoring/PackagesEndPointLogic";
import { CustomDateRangePicker } from "../../../../ReusableDateRangePickerUI/CustomDateRangePicker";
import PackageSkeleton from "./PackageSkeleton";
import { normalizeFilters } from "../packageHelper";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { styled } from "@mui/material/styles";

const tableHeadings = {
  package_name: "Package",
  process_name: "Process Name",
  start_time: "First run",
  end_time: "Last run",
  process_count: "Process Count",
  status: "Status",
  brand: "Brand",
  interface: "Interface",
  run_method: "Run Method",
  run_by: "Run By",
};

const PackagesBase = () => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    packageList,
    isPackagesLoading,
    hasPackageDataBeenFetched,
    selectedPackage,
    selectedProcessName,
    selectedPackageStatus,
    selectedPackageBrand,
    selectedPackageInterface,
    selectedPackageDateRange,
  } = useSelector((state: any) => state.monitoringPackages);

  // -- Get all packages and loading state(Custom Hook)
  const { fetchMonitoringPackages } = useMonitoringPackages();

  const getCurrentFilters = () => {
    const paramPackageName = searchParams.get("package_name");
    return {
      package_name: paramPackageName ? [paramPackageName] : undefined,
      process_id: selectedProcessName?.length ? selectedProcessName : undefined,
      status: selectedPackageStatus?.length ? selectedPackageStatus : undefined,
      brand_id: selectedPackageBrand?.length ? selectedPackageBrand : undefined,
      interface_id: selectedPackageInterface?.length
        ? selectedPackageInterface
        : undefined,
    };
  };

  const filters = normalizeFilters(getCurrentFilters());

  // -- Get packages by Selected Date Range
  const getPackages = () => {
    const from_date =
      selectedPackageDateRange &&
      selectedPackageDateRange?.[0]?.format("YYYY-MM-DD");
    const to_date =
      selectedPackageDateRange &&
      selectedPackageDateRange?.[1]?.format("YYYY-MM-DD");

    // Reset the fetched state before starting new fetch
    dispatch(setPackageDataBeenFetched(false));

    fetchMonitoringPackages(from_date, to_date, 1000, 0, undefined, filters);
    // Reset Redux page
    dispatch(setPage(0));

    // Reset ?page query param and update package_name param
    const updatedParams = new URLSearchParams(window.location.search);
    updatedParams.set("page", "0");
    updatedParams.set("start_date", from_date);
    updatedParams.set("end_date", to_date);
    
    // Add or remove package_name parameter
    if (selectedPackage) {
      updatedParams.set("package_name", selectedPackage);
    } else {
      updatedParams.delete("package_name");
    }
    
    setSearchParams(updatedParams);
  };

  // -- Get all filter options for packages
  useEffect(() => {
    PackagesEndPointLogic.getAllFilters(
      oauthTokenHeader,
      ["status", "BRAND", "process_name", "interface", "package"],
      dispatch
    );
    // eslint-disable-next-line
  }, []);

  //-- Get all packages
  useEffect(() => {
    if (!oauthTokenHeader) return;
    const paramStart = searchParams.get("start_date");
    const paramEnd = searchParams.get("end_date");
    const paramPackageName = searchParams.get("package_name");
    
    // Set package from URL params if exists
    if (paramPackageName !== selectedPackage) {
      dispatch(setSelectedPackage(paramPackageName || ""));
    }
    
    // Create filters based on URL params only
    const initialFilters = normalizeFilters({
      package_name: paramPackageName ? [paramPackageName] : undefined,
      process_id: selectedProcessName?.length ? selectedProcessName : undefined,
      status: selectedPackageStatus?.length ? selectedPackageStatus : undefined,
      brand_id: selectedPackageBrand?.length ? selectedPackageBrand : undefined,
      interface_id: selectedPackageInterface?.length
        ? selectedPackageInterface
        : undefined,
    });
    
    const today = dayjs().format("YYYY-MM-DD");
    const from_date = paramStart ? dayjs(paramStart) : dayjs(today);
    const to_date = paramEnd ? dayjs(paramEnd) : dayjs(today);
    dispatch(setSelectedPackageDateRange([dayjs(from_date), dayjs(to_date)]));

    // Reset the fetched state before starting new fetch
    dispatch(setPackageDataBeenFetched(false));

    fetchMonitoringPackages(
      from_date.format("YYYY-MM-DD"),
      to_date.format("YYYY-MM-DD"),
      1000,
      0,
      undefined,
      initialFilters
    );

    // If no params in URL, set them
    if (!paramStart || !paramEnd) {
      const urlParams: Record<string, string> = {
        start_date: from_date.format("YYYY-MM-DD"),
        end_date: to_date.format("YYYY-MM-DD"),
      };
      
      // Only add package_name if it exists in URL params
      if (paramPackageName) {
        urlParams.package_name = paramPackageName;
      }
      
      setSearchParams(urlParams);
    }
    // eslint-disable-next-line
  }, [oauthTokenHeader, searchParams]);

  
  const packageNames = [...new Set(packageList.map((pkg: any) => pkg.package_name))];
  const LISTBOX_PADDING = 8; // px

  const StyledListbox = styled("div")(() => ({
    padding: 0,
    margin: 0,
    overflow: "auto", // This ensures only the list will scroll
    maxHeight: "calc(100vh - 100px)", // Optional: limits the height to avoid too much space
    display: "block",
  }));
  const renderRow = (props: ListChildComponentProps) => {
    const { data, index, style } = props;
    const dataSet = data[index];
    return React.cloneElement(dataSet, {
      style: {
        ...style,
        top: (style.top as number) + LISTBOX_PADDING,
        padding: "0 8px",
      },
    });
  };
  const ListboxComponent = React.forwardRef(function ListboxComponent(
    props: React.HTMLAttributes<HTMLElement>,
    ref: any
  ) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;
    const itemSize = 32;

    const height = Math.min(8, itemCount) * itemSize + 2 * LISTBOX_PADDING;

    return (
      <StyledListbox ref={ref} {...other}>
        <FixedSizeList
          height={height}
          width="100%"
          itemSize={itemSize}
          itemCount={itemCount}
          itemData={itemData}
          overscanCount={5}
        >
          {renderRow}
        </FixedSizeList>
      </StyledListbox>
    );
  });

  return (
    <>
      <Paper elevation={0} sx={{ padding: "8px 16px" }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h3" sx={{ fontSize: "24px" }}>
              Monitoring
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "14px" }}>
              Inspect process runs (data updating process) and get a macro
              overview of the workflows.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems="center"
              justifyContent={{ xs: "stretch", md: "flex-end" }}
              spacing={1.5}
              sx={{ width: "100%" }}
            >
              <Box sx={{ minWidth: { xs: "100%", sm: 240, md: 260 }, maxWidth: { xs: "100%", sm: 300, md: 320 },flex: { sm: "1 1 auto" }}}>
                <CustomDateRangePicker
                  isDisabled={isPackagesLoading ? true : false}
                  value={selectedPackageDateRange}
                  onChange={(newValue: any) => {
                    const [startDate, endDate] = newValue || [];
                    if (
                      startDate &&
                      endDate &&
                      isGreaterEndDate({ startDate, endDate })
                    ) {
                      dispatch(
                        setDisplayErrorSnackBar({
                          message: "Start date cannot be after end date",
                          type: "error",
                        })
                      );
                      return;
                    }

                    dispatch(setSelectedPackageDateRange([startDate, endDate]));
                    // Removed automatic search params update - user must click "GET DATA"
                  }}
                />
              </Box>
              <Autocomplete
                data-tracker="347"
                disablePortal
                disableClearable={!selectedPackage}
                size="small"
                sx={{ width: 430 }}
                options={packageNames || []}
                value={selectedPackage || ""}
                ListboxComponent={ListboxComponent}
                disabled={isPackagesLoading ? true : false}
                onChange={(_event, newValue) => {
                  dispatch(setSelectedPackage(newValue || ""));
                  
                  // Auto-fetch data ONLY when package is cleared (newValue is null/empty)
                  if (!newValue && selectedPackageDateRange && selectedPackageDateRange[0] && selectedPackageDateRange[1]) {
                    const from_date = selectedPackageDateRange[0].format("YYYY-MM-DD");
                    const to_date = selectedPackageDateRange[1].format("YYYY-MM-DD");
                    
                    // Reset the fetched state before starting new fetch
                    dispatch(setPackageDataBeenFetched(false));
                    
                    // Get updated filters without package selection (no URL package_name)
                    const updatedFilters = normalizeFilters({
                      package_name: undefined,
                      process_id: selectedProcessName?.length ? selectedProcessName : undefined,
                      status: selectedPackageStatus?.length ? selectedPackageStatus : undefined,
                      brand_id: selectedPackageBrand?.length ? selectedPackageBrand : undefined,
                      interface_id: selectedPackageInterface?.length ? selectedPackageInterface : undefined,
                    });
                    
                    fetchMonitoringPackages(from_date, to_date, 1000, 0, undefined, updatedFilters);
                    dispatch(setPage(0));
                    
                    // Update URL params to remove package_name
                    const updatedParams = new URLSearchParams(window.location.search);
                    updatedParams.set("page", "0");
                    updatedParams.set("start_date", from_date);
                    updatedParams.set("end_date", to_date);
                    updatedParams.delete("package_name");
                    setSearchParams(updatedParams);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Package (optional)"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ marginLeft: "12px" }} />
                        </InputAdornment>
                      ),
                    }}
                    color="secondary"
                  />
                )}
              />
              <Button
                data-tracker="348"
                variant="contained"
                color="secondary"
                disabled={
                  isPackagesLoading || // disable while loading
                  !(
                    selectedPackageDateRange &&
                    selectedPackageDateRange[0] &&
                    selectedPackageDateRange[1]
                  )
                }
                onClick={getPackages}
                sx={{
                  padding: "8px 22px",
                }}
              >
                get data
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
      <Paper elevation={0} sx={{ p: 2, marginTop: "10px" }}>
        {isPackagesLoading || !hasPackageDataBeenFetched ? (
          <>
            <PackageSkeleton />
          </>
        ) : (
          <>
            <PackagesFilterCard />
            <PackagesPieChart />
            <PackageTable tableHeadings={tableHeadings} />
          </>
        )}
      </Paper>
    </>
  );
};

export default PackagesBase;
